import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  DocumentArrowUpIcon,
  CircleStackIcon,
  CloudIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../state/language/LanguageProvider';
import { dataConnectionService, connectorService } from '../../services/dataConnection';

interface DataSourceFormData {
  name: string;
  description: string;
  source_type: 'FILE_UPLOAD' | 'DATABASE' | 'API' | 'ERP' | 'DATA_LAKE';
  configuration: Record<string, any>;
}



const DataSourceFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useLanguage();
  const isEditing = Boolean(id);
  
  const [formData, setFormData] = useState<DataSourceFormData>({
    name: '',
    description: '',
    source_type: 'FILE_UPLOAD',
    configuration: {}
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [fileUpload, setFileUpload] = useState<File | null>(null);

  useEffect(() => {
    if (isEditing) {
      fetchDataSource();
    }
  }, [id]);



  const fetchDataSource = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const dataSource = await dataConnectionService.getDataSource(id);
      setFormData({
        name: dataSource.name,
        description: dataSource.description,
        source_type: dataSource.source_type as any,
        configuration: dataSource.configuration
      });
    } catch (error) {
      console.error('Error fetching data source:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof DataSourceFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConfigurationChange = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        [key]: value
      }
    }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFileUpload(file);
      handleConfigurationChange('fileName', file.name);
      handleConfigurationChange('fileSize', file.size);
      handleConfigurationChange('fileType', file.type);
    }
  };

  const testConnection = async () => {
    try {
      setTestResult(null);
      await connectorService.testConnector(formData.source_type, formData.configuration);
      setTestResult({
        success: true,
        message: t('dataSourceForm.testResults.connectionSuccessful')
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : t('dataSourceForm.testResults.connectionFailed')
      });
    }
  };

  const discoverSchema = async () => {
    try {
      setTestResult(null);
      const result = await connectorService.discoverSchema(formData.source_type, formData.configuration);
      setTestResult({
        success: true,
        message: t('dataSourceForm.testResults.schemaDiscovered', { 
          tableCount: result.tables?.length || 0, 
          fieldCount: result.fields?.length || 0 
        })
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : t('dataSourceForm.testResults.schemaDiscoveryFailed')
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert(t('dataSourceForm.pleaseEnterName'));
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await dataConnectionService.updateDataSource(id!, formData);
      } else {
        await dataConnectionService.createDataSource(formData);
      }
      navigate('/data-connection/sources');
    } catch (error) {
      console.error('Error saving data source:', error);
      alert(t('dataSourceForm.errorSavingDataSource'));
    } finally {
      setSaving(false);
    }
  };

  const renderConfigurationForm = () => {
    switch (formData.source_type) {
      case 'FILE_UPLOAD':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                 {t('dataSourceForm.fileUpload.uploadFile')}
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls,.json"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                                         {t('dataSourceForm.fileUpload.chooseFile')}
                  </label>
                </div>
                {fileUpload && (
                  <div className="mt-4 text-sm text-gray-600">
                                         {t('dataSourceForm.fileUpload.selected')}: {fileUpload.name} ({(fileUpload.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                 {t('dataSourceForm.fileUpload.fileProcessingOptions')}
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                                     <label className="block text-xs text-gray-600 mb-1">{t('dataSourceForm.fileUpload.headerRow')}</label>
                  <select
                    value={formData.configuration.hasHeader || 'true'}
                    onChange={(e) => handleConfigurationChange('hasHeader', e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                                         <option value="true">{t('dataSourceForm.fileUpload.headerRowYes')}</option>
                     <option value="false">{t('dataSourceForm.fileUpload.headerRowNo')}</option>
                  </select>
                </div>
                <div>
                                     <label className="block text-xs text-gray-600 mb-1">{t('dataSourceForm.fileUpload.delimiter')}</label>
                  <select
                    value={formData.configuration.delimiter || ','}
                    onChange={(e) => handleConfigurationChange('delimiter', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                                         <option value=",">{t('dataSourceForm.fileUpload.comma')}</option>
                     <option value=";">{t('dataSourceForm.fileUpload.semicolon')}</option>
                     <option value="\t">{t('dataSourceForm.fileUpload.tab')}</option>
                     <option value="|">{t('dataSourceForm.fileUpload.pipe')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      case 'DATABASE':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                     {t('dataSourceForm.database.databaseType')}
                </label>
                <select
                  value={formData.configuration.dbType || 'postgresql'}
                  onChange={(e) => handleConfigurationChange('dbType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                                     <option value="postgresql">{t('dataSourceForm.database.postgresql')}</option>
                   <option value="mysql">{t('dataSourceForm.database.mysql')}</option>
                   <option value="mssql">{t('dataSourceForm.database.mssql')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                     {t('dataSourceForm.database.port')}
                </label>
                <input
                  type="number"
                  value={formData.configuration.port || ''}
                  onChange={(e) => handleConfigurationChange('port', e.target.value)}
                                     placeholder={t('dataSourceForm.database.defaultPort')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                     {t('dataSourceForm.database.host')}
                </label>
                <input
                  type="text"
                  value={formData.configuration.host || ''}
                  onChange={(e) => handleConfigurationChange('host', e.target.value)}
                                     placeholder={t('dataSourceForm.database.hostPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                     {t('dataSourceForm.database.databaseName')}
                </label>
                <input
                  type="text"
                  value={formData.configuration.database || ''}
                  onChange={(e) => handleConfigurationChange('database', e.target.value)}
                                     placeholder={t('dataSourceForm.database.databaseNamePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   {t('dataSourceForm.database.username')}
                 </label>
                <input
                  type="text"
                  value={formData.configuration.username || ''}
                  onChange={(e) => handleConfigurationChange('username', e.target.value)}
                                     placeholder={t('dataSourceForm.database.usernamePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   {t('dataSourceForm.database.password')}
                 </label>
                <input
                  type="password"
                  value={formData.configuration.password || ''}
                  onChange={(e) => handleConfigurationChange('password', e.target.value)}
                                     placeholder={t('dataSourceForm.database.passwordPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={testConnection}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                                 {t('dataSourceForm.database.testConnection')}
              </button>
              <button
                type="button"
                onClick={discoverSchema}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('dataSourceForm.database.discoverSchema')}
              </button>
            </div>
          </div>
        );

      case 'API':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dataSourceForm.api.apiEndpointUrl')}
              </label>
              <input
                type="url"
                value={formData.configuration.url || ''}
                onChange={(e) => handleConfigurationChange('url', e.target.value)}
                                   placeholder={t('dataSourceForm.api.urlPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dataSourceForm.api.authenticationType')}
                </label>
                <select
                  value={formData.configuration.authType || 'none'}
                  onChange={(e) => handleConfigurationChange('authType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                                     <option value="none">{t('dataSourceForm.api.noAuthentication')}</option>
                   <option value="basic">{t('dataSourceForm.api.basicAuth')}</option>
                   <option value="bearer">{t('dataSourceForm.api.bearerToken')}</option>
                   <option value="api_key">{t('dataSourceForm.api.apiKey')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dataSourceForm.api.requestMethod')}
                </label>
                <select
                  value={formData.configuration.method || 'GET'}
                  onChange={(e) => handleConfigurationChange('method', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                                     <option value="GET">{t('dataSourceForm.api.methods.GET')}</option>
                   <option value="POST">{t('dataSourceForm.api.methods.POST')}</option>
                   <option value="PUT">{t('dataSourceForm.api.methods.PUT')}</option>
                </select>
              </div>
            </div>
            
            {formData.configuration.authType === 'basic' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('dataSourceForm.api.username')}
                  </label>
                  <input
                    type="text"
                    value={formData.configuration.apiUsername || ''}
                    onChange={(e) => handleConfigurationChange('apiUsername', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('dataSourceForm.api.password')}
                  </label>
                  <input
                    type="password"
                    value={formData.configuration.apiPassword || ''}
                    onChange={(e) => handleConfigurationChange('apiPassword', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            )}
            
            {formData.configuration.authType === 'bearer' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dataSourceForm.api.bearerToken')}
                </label>
                <input
                  type="text"
                  value={formData.configuration.bearerToken || ''}
                  onChange={(e) => handleConfigurationChange('bearerToken', e.target.value)}
                                     placeholder={t('dataSourceForm.api.bearerTokenPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            )}
            
            {formData.configuration.authType === 'api_key' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('dataSourceForm.api.apiKeyName')}
                  </label>
                  <input
                    type="text"
                    value={formData.configuration.apiKeyName || ''}
                    onChange={(e) => handleConfigurationChange('apiKeyName', e.target.value)}
                    placeholder={t('dataSourceForm.api.apiKeyNamePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('dataSourceForm.api.apiKeyValue')}
                  </label>
                  <input
                    type="text"
                    value={formData.configuration.apiKeyValue || ''}
                    onChange={(e) => handleConfigurationChange('apiKeyValue', e.target.value)}
                    placeholder={t('dataSourceForm.api.apiKeyValuePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={testConnection}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                                 {t('dataSourceForm.api.testAPIConnection')}
              </button>
              <button
                type="button"
                onClick={discoverSchema}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                                 {t('dataSourceForm.api.discoverSchema')}
              </button>
            </div>
          </div>
        );

      case 'ERP':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                 {t('dataSourceForm.erp.erpSystem')}
              </label>
              <select
                value={formData.configuration.erpSystem || 'sap'}
                onChange={(e) => handleConfigurationChange('erpSystem', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                                   <option value="sap">{t('dataSourceForm.erp.sap')}</option>
                   <option value="oracle">{t('dataSourceForm.erp.oracle')}</option>
                   <option value="dynamics">{t('dataSourceForm.erp.dynamics')}</option>
                   <option value="netsuite">{t('dataSourceForm.erp.netsuite')}</option>
                   <option value="custom">{t('dataSourceForm.erp.custom')}</option>
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                     {t('dataSourceForm.erp.connectionType')}
                </label>
                <select
                  value={formData.configuration.erpConnectionType || 'api'}
                  onChange={(e) => handleConfigurationChange('erpConnectionType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                                     <option value="api">{t('dataSourceForm.erp.restAPI')}</option>
                   <option value="odata">{t('dataSourceForm.erp.odata')}</option>
                   <option value="database">{t('dataSourceForm.erp.directDatabase')}</option>
                   <option value="file_export">{t('dataSourceForm.erp.fileExport')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                     {t('dataSourceForm.erp.environment')}
                </label>
                <select
                  value={formData.configuration.erpEnvironment || 'production'}
                  onChange={(e) => handleConfigurationChange('erpEnvironment', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                                     <option value="production">{t('dataSourceForm.erp.production')}</option>
                   <option value="staging">{t('dataSourceForm.erp.staging')}</option>
                   <option value="development">{t('dataSourceForm.erp.development')}</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                                 {t('dataSourceForm.erp.erpEndpointUrl')}
              </label>
              <input
                type="url"
                value={formData.configuration.erpUrl || ''}
                onChange={(e) => handleConfigurationChange('erpUrl', e.target.value)}
                                   placeholder={t('dataSourceForm.erp.erpUrlPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dataSourceForm.erp.username')}
                </label>
                <input
                  type="text"
                  value={formData.configuration.erpUsername || ''}
                  onChange={(e) => handleConfigurationChange('erpUsername', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dataSourceForm.erp.password')}
                </label>
                <input
                  type="password"
                  value={formData.configuration.erpPassword || ''}
                  onChange={(e) => handleConfigurationChange('erpPassword', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={testConnection}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                {t('dataSourceForm.erp.testERPConnection')}
              </button>
              <button
                type="button"
                onClick={discoverSchema}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('dataSourceForm.erp.discoverERPSchema')}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/data-connection/sources')}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {isEditing ? t('dataSourceForm.editDataSource') : t('dataSourceForm.addNewDataSource')}
                </h1>
                <p className="mt-2 text-gray-600">
                  {isEditing ? t('dataSourceForm.updateDataSourceConfig') : t('dataSourceForm.connectToNewDataSource')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">{t('dataSourceForm.basicInformation')}</h2>
            
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dataSourceForm.dataSourceName')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={t('dataSourceForm.dataSourceNamePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder={t('dataSourceForm.descriptionPlaceholder')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dataSourceForm.dataSourceType')}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { type: 'FILE_UPLOAD', label: t('dataSourceForm.types.fileUpload'), icon: DocumentArrowUpIcon },
                    { type: 'DATABASE', label: t('dataSourceForm.types.database'), icon: CircleStackIcon },
                    { type: 'API', label: t('dataSourceForm.types.api'), icon: CloudIcon },
                    { type: 'ERP', label: t('dataSourceForm.types.erpSystem'), icon: BuildingOfficeIcon }
                  ].map((option) => (
                    <button
                      key={option.type}
                      type="button"
                      onClick={() => handleInputChange('source_type', option.type)}
                      className={`p-4 border-2 rounded-lg text-center transition-colors ${
                        formData.source_type === option.type
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <option.icon className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                      <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Configuration */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">{t('dataSourceForm.configuration')}</h2>
            {renderConfigurationForm()}
          </div>

          {/* Test Results */}
          {testResult && (
            <div className={`bg-white rounded-lg shadow p-6 ${
              testResult.success ? 'border-l-4 border-green-400' : 'border-l-4 border-red-400'
            }`}>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('dataSourceForm.testResults')}</h3>
              <div className={`p-4 rounded-md ${
                testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {testResult.message}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/data-connection/sources')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
                             {t('dataSourceForm.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                             {saving ? t('dataSourceForm.saving') : (isEditing ? t('dataSourceForm.updateDataSource') : t('dataSourceForm.createDataSource'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DataSourceFormPage;
